import type { StreamQualityResponse } from "../../api/client";
import { EmptyState, ErrorState, LoadingState } from "@/components/common/states";

interface Props {
  data: StreamQualityResponse | null;
  loading: boolean;
  error: boolean;
}

/**
 * SPEC §14 — santé du flux Lightstreamer (diagnostic infra, page Journal).
 * Contrat trois états (B6 read-only).
 */
export default function StreamQuality({ data, loading, error }: Props) {
  if (error) {
    return (
      <ErrorState title="Qualité du flux indisponible" testId="sq-error">
        Recharge pour réessayer.
      </ErrorState>
    );
  }
  if (loading || data == null) {
    return <LoadingState lines={3} />;
  }
  const noEvents = data.byEventType.length === 0;
  const noTicks = data.quoteAgeMs.sampleSize === 0;
  if (noEvents && noTicks) {
    return (
      <EmptyState testId="sq-empty">
        Aucune activité du flux sur les {data.windowHours} dernières heures.
      </EmptyState>
    );
  }
  return (
    <div className="flex flex-col gap-3" data-testid="sq-data">
      <p className="text-muted-foreground text-sm">{data.windowHours} dernières heures</p>
      <div className="flex flex-col gap-1">
        <h3 className="text-foreground text-xs font-semibold tracking-wide uppercase">
          Événements du flux
        </h3>
        {noEvents ? (
          <p className="text-muted-foreground text-sm">
            Aucun événement connexion / coupure / trou dans la fenêtre.
          </p>
        ) : (
          <table className="data-table w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="text-muted-foreground border-border border-b text-left text-[0.68rem] tracking-wide uppercase">
                <th className="px-2 py-2 font-medium">Type</th>
                <th className="px-2 py-2 font-medium">Compte</th>
              </tr>
            </thead>
            <tbody>
              {data.byEventType.map((e) => (
                <tr
                  key={e.eventType}
                  data-testid={`sq-event-${e.eventType}`}
                  className="border-border/60 border-b font-mono tabular-nums"
                >
                  <td className="px-2 py-1.5"><code>{e.eventType}</code></td>
                  <td className="px-2 py-1.5">{e.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="text-foreground text-xs font-semibold tracking-wide uppercase">
          Âge des quotes (ms)
        </h3>
        {noTicks ? (
          <p className="text-muted-foreground text-sm">Aucun tick dans la fenêtre.</p>
        ) : (
          <dl className="grid grid-cols-[repeat(5,auto)] justify-start gap-x-6 gap-y-1 text-sm [&>dt]:text-muted-foreground [&>dd]:m-0 [&>dd]:font-mono [&>dd]:tabular-nums">
            <dt>Échantillons</dt><dd>{data.quoteAgeMs.sampleSize}</dd>
            <dt>p50</dt><dd>{data.quoteAgeMs.p50 ?? "—"}</dd>
            <dt>p90</dt><dd>{data.quoteAgeMs.p90 ?? "—"}</dd>
            <dt>p99</dt><dd>{data.quoteAgeMs.p99 ?? "—"}</dd>
            <dt>max</dt><dd>{data.quoteAgeMs.max ?? "—"}</dd>
          </dl>
        )}
      </div>
    </div>
  );
}
